import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import {
  CheckCircle2,
  Info,
  ArrowRight,
  Calendar,
  Shield,
  AlertTriangle,
  Snowflake,
  XCircle,
} from "lucide-react";

export default function IceDammingSaskatchewan() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id":
            "https://www.saskroofingcompany.com/blog/ice-damming-saskatchewan#article",
          headline:
            "Ice Damming in Saskatchewan: Why It Happens and How to Prevent It",
          description:
            "Learn why ice dams form on Saskatchewan roofs, the warning signs of winter roof leaks, and exactly how to prevent them from damaging your home.",
          author: { "@id": "https://www.saskroofingcompany.com/#organization" },
          publisher: {
            "@id": "https://www.saskroofingcompany.com/#organization",
          },
          datePublished: "2026-04-01",
          inLanguage: "en-CA",
          isPartOf: {
            "@id": "https://www.saskroofingcompany.com/blog#webpage",
          },
          mainEntityOfPage:
            "https://www.saskroofingcompany.com/blog/ice-damming-saskatchewan",
          url: "https://www.saskroofingcompany.com/blog/ice-damming-saskatchewan",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.saskroofingcompany.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: "https://www.saskroofingcompany.com/blog",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Ice Damming in Saskatchewan",
              item: "https://www.saskroofingcompany.com/blog/ice-damming-saskatchewan",
            },
          ],
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is ice damming and how does it damage roofs?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Ice damming occurs when heat escapes from your attic, melting snow on the roof. This water runs down to the colder eaves and refreezes, forming a dam. As more snow melts, water backs up behind the dam, seeping under your shingles and leaking into your home.",
          },
        },
        {
          "@type": "Question",
          name: "How do I prevent ice damming on my Saskatchewan home?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The most effective way to prevent ice damming is by keeping your roof deck cold. This requires sealing attic bypasses, upgrading attic insulation to Saskatchewan standards (R-50 to R-60), and ensuring adequate intake and exhaust ventilation so cold air can circulate under the roof deck.",
          },
        },
        {
          "@type": "Question",
          name: "Does home insurance cover ice dam damage?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Most standard homeowner's insurance policies cover the sudden and accidental interior water damage caused by an ice dam (like ruined drywall or flooring). However, they typically do not cover the cost of removing the ice dam itself or fixing the underlying insulation/ventilation issues that caused it.",
          },
        },
        {
          "@type": "Question",
          name: "Can ice damming be fixed without replacing the whole roof?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, in many cases. Improving attic insulation and adding proper ventilation can often solve the problem without touching the exterior roof. However, if the shingles are severely damaged or the roof is nearing the end of its lifespan, a full replacement with proper ice and water shield is recommended.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Ice Damming in Saskatchewan: Prevention & Repair Guide"
        description="Learn why ice dams form on Saskatchewan roofs, the warning signs of winter roof leaks, and exactly how to prevent them from damaging your home."
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/ff37d77b-d887-4f64-9492-693b3247af5b.png&output=webp&q=60"
            alt="Large icicles hanging from the eaves of a modern Saskatchewan home during a cold prairie winter"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-4xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/80 text-primary-foreground text-sm font-medium mb-4">
              <Calendar className="w-4 h-4" /> Published April 1, 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Ice Damming in Saskatchewan: Why It Happens and How to Prevent It
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Those beautiful icicles hanging from your gutters? They might be
              destroying your home from the inside out. Here is why ice damming
              plagues Saskatchewan homes and exactly what you can do to stop it.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Intro */}
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p>
                As the deep freeze of a Saskatchewan winter sets in, many
                homeowners notice a picturesque but ominous sight: thick, jagged
                icicles hanging like teeth from the edge of their roof. While
                they might look like a classic winter postcard, these icicles
                are often the most visible symptom of a destructive process
                known as ice damming.
              </p>
              <p>
                Ice damming is a unique winter roofing problem that affects
                thousands of homes across Saskatoon, Regina, Moose Jaw, and the
                rest of the province every year. It happens when the complex
                relationship between your home's heating system, attic
                insulation, and roof ventilation falls out of balance. If left
                unchecked, an ice dam can force hundreds of gallons of meltwater
                backwards under your shingles, leading to devastating interior
                leaks, ruined drywall, and hidden mold growth. This guide breaks
                down the physics of ice dams, the warning signs to watch for,
                and the permanent solutions that can keep your home dry.
              </p>
            </div>

            {/* TL;DR Box */}
            <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-xl shadow-sm">
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground mb-3">
                <Info className="w-6 h-6 text-primary" />
                The Short Answer (TL;DR)
              </h2>
              <p className="text-lg text-muted-foreground">
                Ice dams form when heat escapes your living space, warms the
                attic, and melts the snow on your roof. The water runs down to
                the colder eaves, refreezes into a wall of ice, and forces
                subsequent meltwater backward under your shingles. The permanent
                fix isn't hammering the ice off—it's upgrading your attic
                insulation and ventilation to keep the roof deck cold, or
                scheduling a{" "}
                <Link
                  to="/services/replacement"
                  className="text-primary font-medium hover:underline"
                >
                  roof replacement
                </Link>{" "}
                with extensive ice and water shield membranes.
              </p>
            </div>

            {/* What is Ice Damming Section */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                What Exactly is Ice Damming?
              </h2>
              <p className="text-lg text-muted-foreground">
                To understand ice damming, you have to understand the basic
                physics of heat transfer. Heat rises. In a perfectly designed
                home, the heat from your living space stays in your living
                space, and your attic remains as cold as the outside air.
                However, very few homes are perfect.
              </p>
              <p className="text-lg text-muted-foreground">
                When warm air escapes through ceiling light fixtures, poorly
                sealed attic hatches, or inadequate insulation, it warms the
                underside of the roof decking. This causes the snow sitting on
                top of your roof to melt, even if the outside temperature is
                well below freezing.
              </p>
              <p className="text-lg text-muted-foreground">
                This meltwater trickles down the slope of the roof under the
                snowpack until it reaches the eaves (the overhangs of the roof).
                Because the eaves extend past the exterior walls of your heated
                house, they remain freezing cold. When the liquid water hits
                this freezing zone, it instantly turns back into ice. Over days
                and weeks, this cycle creates a solid ridge of ice—a dam—along
                the edge of your roof.
              </p>
              <p className="text-lg text-muted-foreground font-medium">
                Here is where the damage happens: As more snow melts higher up
                on the roof, the water runs down and hits the dam. With nowhere
                else to go, it pools up. Asphalt shingles are designed to shed
                water flowing downward; they are <em>not</em> designed to hold
                standing water. The pooled water slowly works its way backward,
                creeping up under the shingles, soaking the roof decking, and
                eventually dripping into your attic and living space.
              </p>
            </div>

            {/* Why Saskatchewan Roofs Section */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                Why Saskatchewan Roofs are Highly Susceptible
              </h2>
              <p className="text-lg text-muted-foreground">
                Ice damming happens in many cold climates, but Saskatchewan
                presents a perfect storm of conditions that make our homes
                particularly vulnerable:
              </p>
              <ul className="space-y-4 text-lg text-muted-foreground">
                <li className="flex gap-3">
                  <Snowflake className="w-6 h-6 text-blue-500 shrink-0 mt-1" />
                  <div>
                    <strong>Extreme Temperature Differentials:</strong> When
                    it's -40°C outside and you are heating your home to +21°C
                    inside, the pressure for that heat to escape upward is
                    immense. Even tiny gaps in insulation become major heat
                    leaks.
                  </div>
                </li>
                <li className="flex gap-3">
                  <Snowflake className="w-6 h-6 text-blue-500 shrink-0 mt-1" />
                  <div>
                    <strong>Sustained Snow Loads:</strong> Unlike warmer regions
                    where snow melts off completely between storms, Saskatchewan
                    roofs often hold a thick blanket of snow from November
                    through March. This snow acts as an insulating blanket,
                    trapping the escaping attic heat against the shingles and
                    accelerating the melting process.
                  </div>
                </li>
                <li className="flex gap-3">
                  <Snowflake className="w-6 h-6 text-blue-500 shrink-0 mt-1" />
                  <div>
                    <strong>Aging Housing Stock:</strong> Many homes built in
                    Saskatchewan before the 1990s were constructed with
                    significantly less attic insulation and ventilation than
                    modern building codes require, making them prime candidates
                    for severe heat loss.
                  </div>
                </li>
              </ul>
            </div>

            <div className="my-8 rounded-2xl overflow-hidden shadow-md">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/bcd162ff-e52a-4020-89c3-b1a2f0971c14.png&output=webp&q=60"
                alt="Water damage and brown staining on a white interior ceiling caused by a leaking roof ice dam"
                className="w-full h-auto object-cover"
              />
              <p className="text-sm text-center text-muted-foreground p-3 bg-muted/30 border-t">
                Brown water stains on an exterior wall ceiling are a classic
                indicator that an ice dam has forced water under the shingles.
              </p>
            </div>

            {/* Warning Signs Listicle */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                5 Warning Signs of Ice Damming
              </h2>
              <p className="text-lg text-muted-foreground">
                Catching an ice dam early can save you thousands of dollars in
                interior renovations. Watch for these five critical signs during
                the winter months:
              </p>

              <div className="space-y-6 mt-6">
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Massive Icicles</h3>
                    <p className="text-muted-foreground">
                      Small, thin icicles are normal. But if you have thick
                      icicles the size of soft drink bottles or baseball bats
                      hanging from your eavestroughs, a significant amount of
                      water is freezing at the roof edge.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Ice Filled Gutters
                    </h3>
                    <p className="text-muted-foreground">
                      If your gutters are completely encased in solid ice, or if
                      you can see a thick ridge of ice pushing up underneath the
                      bottom row of shingles, a dam has formed.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Water Stains on Ceilings
                    </h3>
                    <p className="text-muted-foreground">
                      Look at the ceilings inside your home, specifically along
                      the exterior walls. Faint yellow or brown rings indicate
                      that water has breached the roof deck and soaked through
                      the attic insulation.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Peeling Paint Near the Roofline
                    </h3>
                    <p className="text-muted-foreground">
                      If the exterior paint on your siding or fascia boards
                      directly below the roofline is suddenly peeling or
                      blistering in the middle of winter, moisture is trapped
                      behind the wood.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold shrink-0">
                    5
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Water Running Behind Fascia
                    </h3>
                    <p className="text-muted-foreground">
                      If you see icicles forming on the siding of your house or
                      water dripping down the exterior walls (bypassing the
                      gutters entirely), the ice dam has pushed water behind the
                      fascia board.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Callout Card */}
            <div className="bg-accent/10 border border-accent/20 p-6 rounded-xl flex gap-4 my-8">
              <AlertTriangle className="w-8 h-8 text-accent shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                  The Hidden Danger: Mold
                </h3>
                <p className="text-muted-foreground">
                  The water from an ice dam doesn't always drip through the
                  ceiling immediately. Often, it slowly saturates your attic
                  insulation. Wet fiberglass insulation loses its R-value,
                  making the heat loss even worse. Worse still, a damp, dark
                  attic is the perfect breeding ground for toxic black mold,
                  which can spread unnoticed for months until the spring thaw.
                </p>
              </div>
            </div>

            {/* How to Prevent Section */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                How to Prevent Ice Damming
              </h2>
              <p className="text-lg text-muted-foreground">
                Preventing ice dams requires treating the root cause (a warm
                roof deck) rather than just treating the symptom (the ice
                itself). Here is the proven approach for Saskatchewan homes:
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    1. Maximize Attic Insulation
                  </h3>
                  <p className="text-muted-foreground">
                    The goal is to keep the heat in your living room. In
                    Saskatchewan, the recommended attic insulation level is R-50
                    to R-60 (about 16 to 20 inches of blown-in fiberglass or
                    cellulose). Before adding insulation, it is critical to seal
                    all air bypasses—the gaps around plumbing stacks, chimney
                    chases, and pot lights where warm air shoots directly into
                    the attic.
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    2. Balance Attic Ventilation
                  </h3>
                  <p className="text-muted-foreground">
                    Insulation keeps the heat out of the attic, but ventilation
                    flushes away the small amount of heat that inevitably
                    escapes. You need a balanced system: soffit vents (intake)
                    at the bottom to draw in freezing outside air, and ridge
                    vents or maximum vents (exhaust) at the top to let the
                    warmer air out. This constant flow of freezing air keeps the
                    roof deck cold, preventing the snow from melting.
                  </p>
                </div>
              </div>

              <div className="prose prose-lg max-w-none text-muted-foreground">
                <p>
                  <strong>The Ultimate Failsafe: Ice and Water Shield.</strong>{" "}
                  Even with perfect insulation, extreme weather can sometimes
                  cause minor ice buildup. That is why building codes require a
                  specialized rubberized membrane called "ice and water shield"
                  to be installed along the eaves of the roof before the
                  shingles go on.
                </p>
                <p>
                  At Sask Roofing Company, when we perform a{" "}
                  <Link
                    to="/services/replacement"
                    className="text-primary font-medium hover:underline"
                  >
                    roof replacement
                  </Link>
                  , we install premium ice and water shield a minimum of 3 feet
                  up from the exterior wall line (often 6 feet on lower-pitch
                  roofs) and in all roof valleys. This membrane self-seals
                  around the roofing nails, creating a waterproof barrier so
                  that even if water pools up behind an ice dam, it cannot
                  penetrate the roof deck.
                </p>
              </div>
            </div>

            <div className="my-8 rounded-2xl overflow-hidden shadow-md">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/8cf1ae0e-1305-4a6e-a31c-17e154853160.png&output=webp&q=60"
                alt="A snow-covered residential roof in winter with a clear ridge vent system showing proper attic ventilation"
                className="w-full h-auto object-cover"
              />
              <p className="text-sm text-center text-muted-foreground p-3 bg-muted/30 border-t">
                A properly functioning ridge vent allows escaping heat to exit
                the attic, keeping the roof deck cold and preventing snow melt.
              </p>
            </div>

            {/* What NOT to Do */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                What NOT to Do
              </h2>
              <p className="text-lg text-muted-foreground">
                When homeowners panic about a leak, they often make the
                situation worse. Avoid these common mistakes:
              </p>

              <div className="bg-muted/30 p-6 rounded-xl border my-6">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-foreground">
                        Don't attack the ice with a hammer or axe.
                      </p>
                      <p className="text-muted-foreground">
                        You are almost guaranteed to shatter the brittle, frozen
                        asphalt shingles beneath the ice, turning a temporary
                        leak into a permanent hole that requires immediate{" "}
                        <Link
                          to="/services/repair"
                          className="text-primary hover:underline"
                        >
                          roof repair
                        </Link>
                        .
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-foreground">
                        Don't throw rock salt on the roof.
                      </p>
                      <p className="text-muted-foreground">
                        Standard rock salt will aggressively corrode your
                        aluminum eavestroughs and aluminum flashing, and the
                        salty runoff will kill the landscaping around your
                        foundation.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-foreground">
                        Don't open the attic hatch to "let the heat out."
                      </p>
                      <p className="text-muted-foreground">
                        This is a terrible myth. Opening the hatch allows
                        massive amounts of warm, moist air from your home
                        directly into the freezing attic, creating a
                        condensation nightmare that will rain down on your
                        ceiling.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* When to Call a Roofer */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                When to Call a Professional Roofer
              </h2>
              <p className="text-lg text-muted-foreground">
                If you currently have an active leak dripping into your living
                space, call an emergency roofing service immediately.
                Professionals use low-pressure steam units to safely melt the
                ice dam away without damaging the shingles beneath.
              </p>
              <p className="text-lg text-muted-foreground">
                If the leaking has stopped but you noticed water stains, or if
                you had massive icicles all winter, call us in the spring. We
                can assess the damage, check the condition of your existing ice
                and water shield, and provide a comprehensive plan to upgrade
                your roof system before next winter arrives. Planning a roof
                replacement in the off-season ensures your home is protected
                when the deep freeze returns.
              </p>
            </div>

            {/* FAQ */}
            <div className="space-y-6 pt-8 border-t">
              <h2 className="text-3xl font-bold text-foreground">
                Ice Damming FAQ
              </h2>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    What is ice damming and how does it damage roofs?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    Ice damming occurs when heat escapes from your attic,
                    melting snow on the roof. This water runs down to the colder
                    eaves and refreezes, forming a dam. As more snow melts,
                    water backs up behind the dam, seeping under your shingles
                    and leaking into your home.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    How do I prevent ice damming on my Saskatchewan home?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    The most effective way to prevent ice damming is by keeping
                    your roof deck cold. This requires sealing attic bypasses,
                    upgrading attic insulation to Saskatchewan standards (R-50
                    to R-60), and ensuring adequate intake and exhaust
                    ventilation so cold air can circulate under the roof deck.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    Does home insurance cover ice dam damage?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    Most standard homeowner's insurance policies cover the
                    sudden and accidental interior water damage caused by an ice
                    dam (like ruined drywall or flooring). However, they
                    typically do not cover the cost of removing the ice dam
                    itself or fixing the underlying insulation/ventilation
                    issues that caused it.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    Can ice damming be fixed without replacing the whole roof?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    Yes, in many cases. Improving attic insulation and adding
                    proper ventilation can often solve the problem without
                    touching the exterior roof. However, if the shingles are
                    severely damaged or the roof is nearing the end of its
                    lifespan, a full replacement with proper ice and water
                    shield is recommended.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-muted/50 text-center border-t">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl font-bold">Stop the Winter Leaks</h2>
          <p className="text-xl text-muted-foreground">
            Don't let another Saskatchewan winter threaten your home. Book a
            free inspection today. We'll assess your roof, ventilation, and
            insulation to provide a permanent solution to ice damming.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              asChild
            >
              <Link to="/contact">Book a Free Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-primary text-primary hover:bg-primary/5"
              asChild
            >
              <a href="tel:6397397969">Call (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
